// Adapted from T3 Code's html_render tool (apps/server/src/mcp/toolkits/html/tools.ts, handlers.ts) and its
// page guides (packages/shared/src/htmlRender.ts), commit 0ae1091d019484d474fce0fe0a89195a82134041
// (MIT, Copyright 2026 T3 Tools Inc.). Full notice: public/third-party-licenses.txt.

/**
 * The `show_page` tool every /code session offers its agent, through the
 * cubic plugin's session tools (`OPENCODE_PLUGIN_ENV.SESSION_TOOLS_FILE`): the
 * agent shows the user a page it made, such as a chart or a table, above its
 * reply. The plugin answers each call with the fixed result below, and the
 * page is the call's input, which the transcript shows in a sandboxed frame
 * (`agent-page.ts`). The input schema mirrors `showPageInputSchema`; a test
 * keeps the two in step. No Node imports: the session permissions and the
 * browser read it.
 */

import { z } from 'zod';

import type { SessionToolDefinition } from '.';

import {
  AGENT_PAGE_COLUMN_WIDTH,
  AGENT_PAGE_MAX_HEIGHT,
  AGENT_PAGE_MAX_HTML_LENGTH,
  AGENT_PAGE_MAX_TITLE_LENGTH,
  AGENT_PAGE_MIN_HEIGHT,
} from '@/lib/code-sessions/agent-page';

/** The OpenCode tool the agent calls to show the user a page in the conversation. */
export const SHOW_PAGE_TOOL = 'show_page';

export const showPageInputSchema = z.object({
  html: z.string().min(1).max(AGENT_PAGE_MAX_HTML_LENGTH),
  title: z.string().min(1).max(AGENT_PAGE_MAX_TITLE_LENGTH),
  height: z.number().int().min(AGENT_PAGE_MIN_HEIGHT).max(AGENT_PAGE_MAX_HEIGHT),
});

const LAYOUT_GUIDE = [
  `The frame is borderless on the conversation's background, as wide as the reply column (${AGENT_PAGE_COLUMN_WIDTH}px on desktop, about 360px on phones), and its left edge lines up with your reply text.`,
  "The page sits on the conversation's own background, so by default leave html, body, and the outermost element with no background color. This overrides general style preferences such as a fixed black page background.",
  'Use a fluid width with no horizontal padding on the outermost element, and no outer card, border, or banner title: the page is part of your reply.',
  'If a box needs its own background (a mock of a specific screen, a panel that must stand apart), give it at least 16px of padding on every side and var(--radius) corners, so content never touches its edge.',
  'Give charts fixed pixel heights rather than heights that scale with width.',
  "Let content set the page's height. Avoid viewport-based heights such as 100vh or height:100% on html or body; the frame grows to fit the page, so they can make it grow again and again.",
].join(' ');

const THEME_GUIDE = [
  "cubic injects its theme as CSS custom properties on :root, and they follow the user's light or dark mode live:",
  '--background (page background, identical to the conversation around the frame), --foreground, --muted, --muted-foreground,',
  '--card, --card-foreground, --popover, --popover-foreground, --secondary, --secondary-foreground, --border, --input, --ring,',
  '--primary, --primary-foreground (solid buttons), --accent, --accent-foreground (brand accent), --accent-surface, --accent-surface-foreground,',
  '--destructive, --destructive-foreground, --destructive-surface, --warning, --warning-foreground, --warning-surface,',
  '--success, --success-foreground, --info, --info-foreground, --code-background, --code-foreground,',
  '--chart-1, --chart-2, --chart-3, --chart-4, --chart-5, --chart-6 (categorical series for charts), --radius, --font-sans, --font-mono.',
  "The base stylesheet sets html background/color/font from these, body margin to 0, and hides the page's scrollbar; your own CSS overrides it.",
].join(' ');

/** The tool as the plugin registers it: `input` is its JSON Schema, `result` what every accepted call answers. */
export const SHOW_PAGE_TOOL_DEFINITION = {
  name: SHOW_PAGE_TOOL,
  description: [
    `Show the user a finished HTML page (chart, table, diagram, collage, mockup) in this conversation, above your final reply; call it before writing that reply. The user already sees the page, so the reply should not announce it, say where it is, or restate it: add only what the page doesn't say. Check the page in this sandbox's browser first: the show-page skill has the steps. cubic fits the frame to the page's height at the user's width, up to ${AGENT_PAGE_MAX_HEIGHT}px, past which the page scrolls inside it.`,
    `Write one self-contained document with inline <style> and <script> and its data in the page, at most ${AGENT_PAGE_MAX_HTML_LENGTH.toLocaleString('en-US')} characters. It runs sandboxed, away from cubic and the user's session: it has no cookies or storage, its scripts cannot make network requests, scripts from other hosts load only from https://unpkg.com, and stylesheets and fonts only from Google Fonts. Images load from any http(s) URL, but files in this sandbox never reach the user: embed a small image as a data: URL, and share screenshots and videos with share_file instead.`,
    LAYOUT_GUIDE,
    THEME_GUIDE,
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['html', 'title', 'height'],
    properties: {
      html: {
        type: 'string',
        minLength: 1,
        maxLength: AGENT_PAGE_MAX_HTML_LENGTH,
        description: 'A complete, self-contained HTML document.',
      },
      title: {
        type: 'string',
        minLength: 1,
        maxLength: AGENT_PAGE_MAX_TITLE_LENGTH,
        description: 'Short name for the page.',
      },
      height: {
        type: 'integer',
        minimum: AGENT_PAGE_MIN_HEIGHT,
        maximum: AGENT_PAGE_MAX_HEIGHT,
        description: `The page's height in CSS pixels at ${AGENT_PAGE_COLUMN_WIDTH}px wide, which the show-page skill measures, ${AGENT_PAGE_MIN_HEIGHT}-${AGENT_PAGE_MAX_HEIGHT}. cubic shows the frame at this height until the page reports its own.`,
      },
    },
  },
  needs: 'conversation',
  // What the model reads before its reply, which the page shows above.
  result:
    "The user sees the page above your reply. Don't mention or describe it; reply with only what it doesn't already say.",
} as const satisfies SessionToolDefinition;
